import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useRemoteContent } from "@/lib/ContentContext";
import ProductCard from "@/pages/products/components/ProductCard";

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://example.com";

const activeCatMap: Record<string, string> = {
  cyan: "border-cyan-500/60 bg-cyan-500/10 text-cyan-400",
  orange: "border-orange-500/60 bg-orange-500/10 text-orange-400",
  emerald: "border-emerald-500/60 bg-emerald-500/10 text-emerald-400",
  violet: "border-violet-500/60 bg-violet-500/10 text-violet-400",
};

const categoryIcons: Record<string, string> = {
  "aero-engine": "ri-fire-line",
  "uav-propulsion": "ri-flight-takeoff-line",
  "precision-mfg": "ri-settings-3-line",
  "tech-services": "ri-flask-line",
};

function getCatIcon(catId: string) {
  return categoryIcons[catId] ?? "ri-apps-line";
}

function getCatAccent(catId: string) {
  const map: Record<string, string> = {
    "aero-engine": "cyan",
    "uav-propulsion": "orange",
    "precision-mfg": "emerald",
    "tech-services": "violet",
  };
  return map[catId] ?? "cyan";
}

export default function ProductsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [lang, setLang] = useState<"zh" | "en">("zh");
  const [searchKeyword, setSearchKeyword] = useState("");

  const siteContent = useRemoteContent();
  const products = siteContent.productsDetail;
  const newsCount = siteContent.newsArticles.length;
  const productCategories = siteContent.productCategories;
  const navContent = siteContent.nav;

  const activeCat = searchParams.get("cat") || "all";

  useEffect(() => {
    const storedLang = localStorage.getItem("site-lang");
    if (storedLang === "en" || storedLang === "zh") setLang(storedLang as "zh" | "en");
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [activeCat]);

  useEffect(() => {
    document.title = "产品中心 | 航空发动机·无人机动力·精密制造 - 星核动力";
    const metaDesc = document.querySelector<HTMLMetaElement>("meta[name='description']");
    if (metaDesc) {
      metaDesc.content =
        "星核动力产品中心：涡扇发动机、涡轴发动机、活塞发动机、无人机电动推进、混合动力系统、涡轮叶片精密加工、适航认证服务，全系航空推进产品一站查询。";
    }
    const schema = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "产品中心 - 星核动力科技",
      description: "星核动力产品中心：航空发动机、无人机动力系统、精密制造及技术服务全线产品",
      url: `${SITE_URL}/products`,
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.textContent = JSON.stringify(schema);
    script.setAttribute("data-schema", "products");
    document.head.appendChild(script);
    return () => {
      script.parentNode?.removeChild(script);
      document.title = "星核动力 | 航空发动机·无人机动力·精密制造";
    };
  }, []);

  const filteredProducts = products.filter((p) => {
    const matchCat = activeCat === "all" || p.category === activeCat;
    const kw = searchKeyword.trim().toLowerCase();
    const matchKw =
      !kw ||
      p.zh.title.toLowerCase().includes(kw) ||
      p.en.title.toLowerCase().includes(kw) ||
      p.zh.subtitle.toLowerCase().includes(kw) ||
      p.en.subtitle.toLowerCase().includes(kw) ||
      p.zh.desc.toLowerCase().includes(kw) ||
      p.en.desc.toLowerCase().includes(kw) ||
      p.zh.tags.some((t) => t.toLowerCase().includes(kw)) ||
      p.en.tags.some((t) => t.toLowerCase().includes(kw));
    return matchCat && matchKw;
  });

  const handleCat = (cat: string) => {
    if (cat === "tech-services") {
      navigate("/news");
      return;
    }
    setSearchParams(cat === "all" ? {} : { cat });
  };

  const allCategories = [
    { id: "all", zh: "全部产品", en: "All Products" },
    ...productCategories,
  ];

  return (
    <div className="min-h-screen bg-[#070c1a] text-white">
      {/* Top Nav Bar */}
      <nav className="fixed top-0 left-0 w-full z-50 bg-[#0a0f1e]/95 backdrop-blur-md border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-16">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-gray-400 hover:text-cyan-400 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line text-lg" />
            <span className="text-sm font-medium">{lang === "zh" ? "返回首页" : "Back to Home"}</span>
          </button>
          <div className="flex items-center gap-3">
            <img
              src={navContent.logoImage}
              alt={navContent.logoAlt}
              className="h-9 w-auto object-contain cursor-pointer"
              onClick={() => navigate("/")}
            />
          </div>
          <button
            onClick={() => {
              const nl = lang === "zh" ? "en" : "zh";
              setLang(nl);
              localStorage.setItem("site-lang", nl);
            }}
            className="flex items-center gap-1 px-3 py-1.5 rounded-full border border-cyan-500/40 text-cyan-400 text-xs font-semibold hover:bg-cyan-500/10 transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-translate-2 text-sm" />
            {lang === "zh" ? "EN" : "中文"}
          </button>
        </div>
      </nav>

      {/* Category Filter + Search */}
      <div className="sticky top-16 z-40 bg-[#070c1a]/95 backdrop-blur-md border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 flex items-center gap-4">
          <div className="flex items-center gap-2 overflow-x-auto scrollbar-none flex-1">
            {allCategories.map((cat) => {
              const accent = cat.id === "all" ? "cyan" : getCatAccent(cat.id);
              const isActive = activeCat === cat.id;
              const icon = cat.id === "all" ? "ri-apps-line" : getCatIcon(cat.id);
              return (
                <button
                  key={cat.id}
                  onClick={() => handleCat(cat.id)}
                  className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full border text-xs font-semibold whitespace-nowrap transition-all duration-300 cursor-pointer ${
                    isActive
                      ? activeCatMap[accent]
                      : "border-white/10 text-gray-500 hover:text-gray-300 hover:border-white/20"
                  }`}
                >
                  <i className={`${icon} text-sm`} />
                  {lang === "zh" ? cat.zh : cat.en}
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${isActive ? "bg-white/10" : "bg-white/5"}`}>
                      {cat.id === "all"
                        ? products.length
                        : cat.id === "tech-services"
                          ? newsCount
                        : products.filter((p) => p.category === cat.id).length}
                    </span>
                  </button>
                );
              })}
          </div>
          <div className="relative flex-shrink-0 w-52">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm pointer-events-none" />
            <input
              type="text"
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              placeholder={lang === "zh" ? "搜索产品名称..." : "Search products..."}
              className="w-full bg-white/5 border border-white/10 rounded-full pl-8 pr-8 py-1.5 text-sm text-gray-300 placeholder-gray-600 focus:outline-none focus:border-cyan-500/50 focus:bg-white/8 transition-all duration-300"
            />
            {searchKeyword && (
              <button
                onClick={() => setSearchKeyword("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-sm" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Products Grid */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-16">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-lg md:text-xl font-bold text-white">
              {searchKeyword.trim()
                ? lang === "zh" ? `"${searchKeyword}" 的搜索结果` : `Results for "${searchKeyword}"`
                : activeCat === "all"
                  ? lang === "zh" ? "全部产品" : "All Products"
                  : lang === "zh"
                    ? productCategories.find((c) => c.id === activeCat)?.zh
                    : productCategories.find((c) => c.id === activeCat)?.en}
            </h2>
            <p className="text-gray-500 text-xs mt-0.5">
              {lang === "zh" ? `共 ${filteredProducts.length} 款产品` : `${filteredProducts.length} products found`}
            </p>
          </div>
          {searchKeyword.trim() && (
            <button
              onClick={() => setSearchKeyword("")}
              className="flex items-center gap-1 text-xs text-gray-500 hover:text-cyan-400 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-close-circle-line" />
              {lang === "zh" ? "清除搜索" : "Clear search"}
            </button>
          )}
        </div>

        {filteredProducts.length === 0 && (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-white/5 mb-4">
              <i className="ri-search-line text-2xl text-gray-600" />
            </div>
            <p className="text-gray-400 text-sm font-medium mb-1">
              {lang === "zh" ? "未找到相关产品" : "No products found"}
            </p>
            <p className="text-gray-600 text-xs mb-4">
              {lang === "zh" ? `没有匹配 "${searchKeyword}" 的产品` : `No results matching "${searchKeyword}"`}
            </p>
            <button
              onClick={() => setSearchKeyword("")}
              className="px-4 py-1.5 rounded-full border border-cyan-500/40 text-cyan-400 text-xs font-semibold hover:bg-cyan-500/10 transition-all cursor-pointer whitespace-nowrap"
            >
              {lang === "zh" ? "清除搜索" : "Clear search"}
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-7">
          {filteredProducts.map((p) => (
            <ProductCard
              key={p.id}
              product={p}
              lang={lang}
              searchKeyword={searchKeyword}
              categoryLabel={
                lang === "zh"
                  ? productCategories.find((c) => c.id === p.category)?.zh ?? ""
                  : productCategories.find((c) => c.id === p.category)?.en ?? ""
              }
            />
          ))}
        </div>
      </div>

      <div className="border-t border-white/5 py-8 text-center">
        <p className="text-gray-600 text-xs">
          &copy; 2024 {lang === "zh" ? "星核动力科技有限公司 · 版权所有" : "Xinghe Propulsion Technology Co., Ltd. · All Rights Reserved"}
        </p>
      </div>
    </div>
  );
}
